<template>
  <div class="text-wrapper" :class="{ centered: centered }" @click="onClick">
    <p
      ref="input"
      :class="[
        'input',
        size,
        textColor,
        {
          'multi-line': multiLine,
          'single-line': !multiLine,
          'never-break': neverBreak
        }
      ]"
      :contentEditable="editable"
      @input="onInput"
      @blur="onBlur"
      @keypress="$emit('keypress', $event)"
      @focus="onFocus"
      @keydown="onKeyDown"
      @mousedown="onMouseDown"
      @paste="onPaste"
    />
    <p :class="['placeholder', size]" v-if="isPlaceholderVisible">{{ placeholder }}</p>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { AutosaveManager } from '@/util/AutosaveManager'

const textTypes: string[] = [
  'title-strong-L',
  'title-strong-M',
  'title-strong-S',
  'heading-semibold-L',
  'heading-semibold-M',
  'heading-semibold-S',
  'paragraph-semibold-M',
  'paragraph-semibold-S',
  'paragraph-regular-M',
  'paragraph-regular-S',
  'label-strong-M',
  'label-strong-S',
  'label-strong-XS',
  'label-strong-XXS',
  'label-medium-M',
  'label-medium-S',
  'label-medium-XS',
  'label-medium-XXS',
  'label-light-M',
  'label-light-S',
  'label-light-XS',
  'label-light-XXS',
  'overline-M',
  'overline-S'
]

/** A key that types a character: named keys (Tab, Escape, Home…) have longer names, and shortcuts carry a modifier */
function insertsCharacter(event: KeyboardEvent): boolean {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
}

@Component
export default class NText extends Vue {
  @Prop({ type: String }) placeholder?: string
  @Prop({ type: String }) value!: string
  @Prop({ type: Number }) maxlength?: number
  @Prop({
    default: 'paragraph-regular-M',
    type: String,
    validator: (prop: string) => textTypes.includes(prop)
  })
  size!: string
  @Prop({ default: 'black', type: String, validator: (prop: string) => ['black', 'sand'].includes(prop) })
  textColor!: string
  @Prop({ type: Boolean, default: false }) editable?: boolean
  @Prop({ type: Boolean, default: false }) multiLine?: boolean
  @Prop({ type: Boolean, default: false }) neverBreak?: boolean
  @Prop({ type: Boolean, default: false }) centered!: boolean
  @Prop({ type: Number, default: 500 }) autosaveInterval!: number
  @Prop({ type: Boolean, default: true }) saveOnBlur!: boolean
  private autosaveManager!: AutosaveManager

  isPlaceholderVisible = false
  private isFocused = false

  @Ref('input') inputRef!: HTMLElement

  @Watch('value') watchValue(newValue?: string) {
    if (this.isFocused && document.activeElement === this.inputRef) {
      this.isPlaceholderVisible = !this.inputRef.innerText.length
      return
    }
    this.resetTo(newValue)
  }
  resetEditedText() {
    this.resetTo(this.value)
  }
  resetTo(value?: string) {
    this.isPlaceholderVisible = !value?.length
    if (this.inputRef.innerText !== value) {
      this.inputRef.innerText = value || ''
    }
  }

  mounted() {
    this.autosaveManager = new AutosaveManager(() => this.autoSaveText(), this.autosaveInterval)
    this.$nextTick(() => {
      this.isPlaceholderVisible = !this.value?.length
      this.inputRef.innerText = this.value || ''
    })
  }

  onInput(_event: Event) {
    this.cleanupBrTags()
    // Read innerText after cleanup to ensure we get the clean value
    const cleanText = this.inputRef.innerText
    this.isPlaceholderVisible = !cleanText.length
    this.autosaveManager.startTimer()
    this.$emit('input', cleanText)
    this.resetScroll()
  }

  blur() {
    this.inputRef.blur()
  }

  focus() {
    this.inputRef.focus()
  }

  /** Focuses with the caret after the last character; a plain focus() on a contenteditable puts it before the first */
  focusAtEnd() {
    this.focusInput(true)
  }

  /**
   * Shows `value` focused, with the caret at `caretOffset`: an undo or redo of this text, made where the edit was.
   * Unlike a `value` change, it also replaces the text while the input is focused.
   */
  showText(value: string, caretOffset: number) {
    this.resetTo(value)
    this.inputRef.focus()
    this.restoreCursorPosition(Math.min(caretOffset, value.length))
  }

  private focusInput(placeAtEnd = false) {
    this.inputRef.focus()
    if (placeAtEnd) {
      this.placeCursorAtEnd()
      // A programmatic caret does not scroll a long single-line text; show the end where the caret is
      this.inputRef.scrollLeft = this.inputRef.scrollWidth
    }
  }

  onClick(event: MouseEvent) {
    this.$emit('click', event)
    if (!this.editable) return
    if (event.target !== this.inputRef) {
      this.focusInput(true)
    }
  }

  onBlur(event: InputEvent) {
    this.isFocused = false
    this.autosaveManager.stopTimer()
    this.$emit('blur', event)
    if (this.saveOnBlur) {
      this.autoSaveText()
    }
    this.resetScroll()
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
    } else if (this.maxlength && this.inputRef.innerText.length >= this.maxlength && insertsCharacter(event)) {
      // Only keys that type: blocking the rest trapped keyboard focus in a full field and took away copy and undo
      event.preventDefault()
    }
    this.$emit('keydown', event)
  }

  onFocus(event: FocusEvent) {
    this.isFocused = true
    this.$emit('focus', event)
  }

  onMouseDown() {
    if (this.editable) {
      this.inputRef.addEventListener('mousemove', this.onMouseMove)
      document.addEventListener('mouseup', this.onMouseUp)
    }
  }

  onMouseMove(event: MouseEvent) {
    if (this.editable) {
      const { inputRef } = this
      const rect = inputRef.getBoundingClientRect()
      const scrollMargin = 10
      if (event.clientX - rect.left < scrollMargin) {
        inputRef.scrollLeft -= scrollMargin
      } else if (rect.right - event.clientX < scrollMargin) {
        inputRef.scrollLeft += scrollMargin
      }
    }
  }

  onMouseUp() {
    if (this.editable) {
      this.inputRef.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('mouseup', this.onMouseUp)
    }
  }

  resetScroll() {
    this.inputRef.scrollTop = 0
    this.inputRef.scrollLeft = 0
  }

  getTextWidth() {
    return this.inputRef.clientWidth + 'px'
  }

  beforeDestroy() {
    this.autosaveManager.stopTimer()
    if (this.saveOnBlur) {
      this.autoSaveText()
    }
  }

  autoSaveText() {
    if (this.value !== this.inputRef.innerText) {
      this.$emit('autosaveText', this.inputRef.innerText)
    }
  }

  cancelPendingAutosave() {
    this.autosaveManager.stopTimer()
  }

  getEditedText(): string {
    return this.inputRef.innerText
  }

  commitText() {
    this.autosaveManager.stopTimer()
    this.autoSaveText()
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault()
    // The text is single-line: pasted line breaks would render as <br>, which cleanupBrTags strips, gluing the lines
    // together. Join them with a space, or keep the first line and hand the rest to a `pasteLines` listener.
    const rawLines = (event.clipboardData?.getData('text/plain') ?? '').split(/\r?\n/)
    const lines = rawLines.map(line => line.trim()).filter(Boolean)
    const splitLines = lines.length > 1 && !!this.$listeners.pasteLines
    const singleLine = lines.length === 1 ? rawLines.find(line => line.trim()) : undefined
    let text = singleLine !== undefined ? this.trimAtTextEdges(singleLine) : splitLines ? lines[0] : lines.join(' ')
    if (text && this.maxlength) {
      const selected = window.getSelection()?.toString().length ?? 0
      text = text.slice(0, Math.max(0, this.maxlength - this.inputRef.innerText.length + selected))
    }
    const inserted = text ? this.insertTextAtCursor(text) : null
    // Clean up any <br> tags that may have been left after deletion
    this.cleanupBrTags()
    // Trigger input logic to maintain consistency with normal typing
    const cleanText = this.inputRef.innerText
    this.isPlaceholderVisible = !cleanText.length
    this.autosaveManager.startTimer()
    this.$emit('input', cleanText)
    this.resetScroll()
    if (inserted) this.revealPastedText(inserted)
    if (splitLines) {
      this.$emit('pasteLines', lines.slice(1))
    }
  }

  /**
   * Keeps a single-line paste's spaces next to existing text, so " pasted" is not glued to the word before the caret,
   * but drops them at the start or end of the field, where they would be saved into the value.
   */
  private trimAtTextEdges(text: string): string {
    const selection = window.getSelection()
    if (!selection?.rangeCount) return text.trim()
    const caret = selection.getRangeAt(0)
    const before = document.createRange()
    before.selectNodeContents(this.inputRef)
    before.setEnd(caret.startContainer, caret.startOffset)
    const after = document.createRange()
    after.selectNodeContents(this.inputRef)
    after.setStart(caret.endContainer, caret.endOffset)
    const trimmedStart = before.toString() ? text : text.trimStart()
    return after.toString() ? trimmedStart : trimmedStart.trimEnd()
  }

  private insertTextAtCursor(text: string): Text | null {
    const selection = window.getSelection()
    if (!selection?.rangeCount) return null

    const range = selection.getRangeAt(0)
    range.deleteContents()

    const textNode = document.createTextNode(text)
    range.insertNode(textNode)

    // Move the cursor to the end of the inserted text
    range.setStartAfter(textNode)
    range.setEndAfter(textNode)
    selection.removeAllRanges()
    selection.addRange(range)
    return textNode
  }

  /** Scrolls a long single-line text so the end of `node`, where the caret is after a paste, is visible */
  private revealPastedText(node: Node) {
    const range = document.createRange()
    range.selectNodeContents(node)
    const overflow = range.getBoundingClientRect().right - this.inputRef.getBoundingClientRect().right
    if (overflow > 0) this.inputRef.scrollLeft += overflow
  }

  private cleanupBrTags() {
    if (!this.inputRef) return

    const brTags = this.inputRef.querySelectorAll('br')
    if (brTags.length === 0) return

    // Save cursor position as text offset (more reliable than range cloning)
    const selection = window.getSelection()
    let savedOffset: number | null = null

    if (selection?.rangeCount) {
      try {
        const range = selection.getRangeAt(0)
        if (this.inputRef.contains(range.commonAncestorContainer)) {
          const preRange = document.createRange()
          preRange.selectNodeContents(this.inputRef)
          preRange.setEnd(range.startContainer, range.startOffset)
          savedOffset = preRange.toString().length
        }
      } catch {
        // Ignore errors, continue without saving position
      }
    }

    // Remove all <br> tags
    brTags.forEach(br => br.remove())

    // Restore cursor position if saved
    if (savedOffset !== null && selection) {
      this.restoreCursorPosition(savedOffset)
    }
  }

  private restoreCursorPosition(offset: number) {
    const selection = window.getSelection()
    if (!selection) return

    try {
      const range = document.createRange()
      let currentOffset = 0

      // Walk through text nodes to find the correct position
      const walker = document.createTreeWalker(this.inputRef, NodeFilter.SHOW_TEXT, null)

      let node: Node | null
      while ((node = walker.nextNode())) {
        const nodeLength = node.textContent?.length || 0
        if (currentOffset + nodeLength >= offset) {
          range.setStart(node, Math.min(offset - currentOffset, nodeLength))
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
          return
        }
        currentOffset += nodeLength
      }

      // Offset beyond all text - place at end
      range.selectNodeContents(this.inputRef)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    } catch {
      // If restoration fails, place cursor at end as fallback
      try {
        const range = document.createRange()
        range.selectNodeContents(this.inputRef)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      } catch {
        selection.removeAllRanges()
      }
    }
  }

  private placeCursorAtEnd() {
    const selection = window.getSelection()
    if (!selection) return
    try {
      const range = document.createRange()
      range.selectNodeContents(this.inputRef)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    } catch {
      selection.removeAllRanges()
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.text-wrapper {
  cursor: text;
  width: 100%;
  overflow-x: hidden;
  display: flex;
  align-items: center;
  position: relative;

  &.centered {
    display: flex;
    justify-content: center;

    .input {
      text-align: center;
    }
  }
}

.input {
  overflow-x: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  box-sizing: border-box;
  min-width: 1px;

  &:focus {
    text-overflow: unset;
    white-space: normal;
  }
}

.sand {
  color: $notch-color-neutral-sand-600;
}

.single-line {
  white-space: nowrap;
}

.multi-line {
  white-space: normal;
  overflow-wrap: break-word;
}

.never-break {
  white-space: nowrap;
  &:focus {
    white-space: nowrap;
  }
}

.placeholder {
  background-color: transparent;
  color: $notch-color-neutral-sand-500;
  pointer-events: none;
  white-space: nowrap;
}

.title-strong-L {
  @include notch-text-title-strong-L;
}

.title-strong-M {
  @include notch-text-title-strong-M;
}

.title-strong-S {
  @include notch-text-title-strong-S;
}

.heading-semibold-L {
  @include notch-text-heading-semibold-L;
}

.heading-semibold-M {
  @include notch-text-heading-semibold-M;
}

.heading-semibold-S {
  @include notch-text-heading-semibold-S;
}

.paragraph-semibold-M {
  @include notch-text-paragraph-semibold-M;
}

.paragraph-semibold-S {
  @include notch-text-paragraph-semibold-S;
}

.paragraph-regular-M {
  @include notch-text-paragraph-regular-M;
}

.paragraph-regular-S {
  @include notch-text-paragraph-regular-S;
}

.label-strong-M {
  @include notch-text-label-strong-M;
}

.label-strong-S {
  @include notch-text-label-strong-S;
}

.label-strong-XS {
  @include notch-text-label-strong-XS;
}

.label-strong-XXS {
  @include notch-text-label-strong-XXS;
}

.label-medium-M {
  @include notch-text-label-medium-M;
}

.label-medium-S {
  @include notch-text-label-medium-S;
}

.label-medium-XS {
  @include notch-text-label-medium-XS;
}

.label-medium-XXS {
  @include notch-text-label-medium-XXS;
}

.label-light-M {
  @include notch-text-label-light-M;
}

.label-light-S {
  @include notch-text-label-light-S;
}

.label-light-XS {
  @include notch-text-label-light-XS;
}

.label-light-XXS {
  @include notch-text-label-light-XXS;
}

.overline-M {
  @include notch-text-overline-M;
}

.overline-S {
  @include notch-text-overline-S;
}
</style>
